import * as React from 'react';
import { GenericGrid } from '../../components/GenericGrid';
import {
    Add24Regular
} from "@fluentui/react-icons";
import { TableCellLayout, TableColumnDefinition, ToolbarButton, createTableColumn } from '@fluentui/react-components';
import { useNavigate, useParams } from 'react-router-dom';
import { License } from '../../dto/License';
import { DateText } from '../../components/DateText';
import { TruncatedText } from '../../components/TruncatedText';
import { ensureSuccess, handleError } from '../../util/ErrorHandling';
import { useGlobalDispatch } from '../../state/GlobalStateProvider';
import { RetrieveMultipleResponse } from '../../dto/RetrieveMultipleResponse';

const rowSelector = (item: License) => item.guid;

export const Licenses: React.FC<any> = () => {
    const { publisherId } = useParams();
    const navigate = useNavigate();
    const globalDispatch = useGlobalDispatch();

    const [licenses, setLicenses] = React.useState<License[] | null>(null);

    React.useEffect(() => {
        if (publisherId) {
            fetch(`/api/publishers/${publisherId}/licenses`, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(r => ensureSuccess(r))
                .then(r => r.json() as Promise<RetrieveMultipleResponse<License>>)
                .then(r => r.records)
                .then(setLicenses)
                .catch(e => handleError(e, globalDispatch));
        }
    }, [publisherId, globalDispatch]);

    const columns: TableColumnDefinition<License>[] = React.useMemo(() => [
        createTableColumn<License>({
            columnId: "Product",
            compare: (a, b) => {
                return (a.productId?.name ?? "").localeCompare(b.productId?.name ?? "");
            },
            renderHeaderCell: () => {
                return "Product";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={item.productId?.name} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<License>({
            columnId: "Customer",
            compare: (a, b) => {
                return (a.customerId?.name ?? "").localeCompare(b.customerId?.name ?? "");
            },
            renderHeaderCell: () => {
                return "Customer";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={item.customerId?.name} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<License>({
            columnId: "Environments",
            compare: (a, b) => {
                return (a.environments?.map(e => e.name)?.sort()?.join(", ") ?? "").localeCompare(b.environments?.map(e => e.name)?.sort()?.join(", ") ?? "");
            },
            renderHeaderCell: () => {
                return "Environments";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={(item.environments?.map(e => e.name)?.sort()?.join(", ") ?? "")} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<License>({
            columnId: "Modified On",
            compare: (a, b) => {
                return a.modifiedOn.localeCompare(b.modifiedOn);
            },
            renderHeaderCell: () => {
                return "Modified On";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <DateText dateString={item.modifiedOn} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<License>({
            columnId: "Modified By",
            compare: (a, b) => {
                return a.modifiedBy?.name.localeCompare(b.modifiedBy?.name);
            },
            renderHeaderCell: () => {
                return "Modified By";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={item.modifiedBy?.name} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<License>({
            columnId: "Created On",
            compare: (a, b) => {
                return a.createdOn.localeCompare(b.createdOn);
            },
            renderHeaderCell: () => {
                return "Created On";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <DateText dateString={item.createdOn} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<License>({
            columnId: "Created By",
            compare: (a, b) => {
                return a.createdBy?.name?.localeCompare(b.createdBy?.name);
            },
            renderHeaderCell: () => {
                return "Created By";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={item.createdBy?.name} />
                    </TableCellLayout>
                );
            },
        })
    ], []);

    const buttons = <>
        <ToolbarButton
            aria-label="Add new record"
            appearance="primary"
            icon={<Add24Regular />}
            onClick={() => navigate(`/workspace/publishers/${publisherId}/licenses/new`)}
        />
    </>

    return (
        <div style={{ borderRadius: "5px", width: "100%", height: "100%", padding: "10px" }}>
            <GenericGrid onRowClick={(rowId) => navigate(`/workspace/publishers/${publisherId}/licenses/${rowId}`)} columns={columns} items={licenses} rowSelector={rowSelector} actionButtons={buttons} />
        </div>
    );
};